import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, Tooltip, CircularProgress, Chip } from "@mui/material";
import { MessageSquare, X, Maximize2, Minimize2, Send, RotateCcw, Bot, Wifi, WifiOff } from "lucide-react";
import ChatMessage from "./ChatMessage";
import { chatConfigured, sendChatMessage, pingChat, getChatHistory } from "../../api/chat_api";

/**
 * The research chat — the factor-alpha Telegram bot, inside the web app.
 *
 * One codebase, two interfaces: every message goes to the same handler the
 * Telegram loop calls, so `/fa NVDA` here is `/fa NVDA` there. What this
 * component adds is the browser's share of the deal: a floating panel on
 * every page, a maximised view where the bot's tables render as tables, and
 * knowledge of which stock page you are on so "how does it look" needs no
 * ticker.
 */

const STORE_KEY = "fa_chat_history_v1";
const MAX_STORED = 60;
const STOCK_ROUTE = /^\/stocks\/([^/]+)\/details/i;

const QUICK_WITH_TICKER = [
    ["Look", "/look"], ["FA grades", "/fa"], ["Fair value", "/fv"], ["Intrinsic value", "/iv"],
    ["Thesis", "/thesis"], ["Earnings review", "/review"], ["Catalysts", "/catalysts"], ["News tone", "/news"], ["Vol", "/vol"],
];
const QUICK_GENERAL = [
    ["Help", "/help"], ["FactorAlpha commands", "/fahelp"], ["Our model top", "/fmtop"], ["News scan", "/news_scan"], ["Consensus moves", "/estimate_moves"],
];

const loadHistory = () => {
    try {
        const raw = sessionStorage.getItem(STORE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
};
const saveHistory = (msgs) => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(msgs.slice(-MAX_STORED))); } catch { /* quota / private mode */ }
};

let _seq = 0;
const mid = () => `${Date.now()}-${(_seq += 1)}`;

const ChatWidget = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [maximised, setMaximised] = useState(false);
    const [messages, setMessages] = useState(loadHistory);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [link, setLink] = useState("unknown"); // unknown | online | offline
    const [who, setWho] = useState(null);
    const [unread, setUnread] = useState(0);

    const listRef = useRef(null);
    const inputRef = useRef(null);

    const ticker = useMemo(() => {
        const m = STOCK_ROUTE.exec(location.pathname);
        return m ? decodeURIComponent(m[1]).toUpperCase() : null;
    }, [location.pathname]);

    /* ── lifecycle ── */
    useEffect(() => { saveHistory(messages); }, [messages]);

    useEffect(() => {
        if (!open) return;
        setUnread(0);
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        // Focus after the panel has rendered.
        const t = setTimeout(() => inputRef.current?.focus(), 60);
        return () => clearTimeout(t);
    }, [open, maximised, messages.length]);

    const onAuthExpired = useCallback(() => {
        sessionStorage.clear();
        navigate("/login", { replace: true });
    }, [navigate]);

    const checkLink = useCallback(async () => {
        const res = await pingChat();
        if (res.authExpired) { onAuthExpired(); return; }
        setLink(res.success ? "online" : "offline");
        if (!res.success) return;
        setWho(res.data?.user || null);
        // The bot keeps this user's conversation server-side (same record
        // Telegram would see). If this browser has nothing, show that.
        if (messages.length === 0) {
            const h = await getChatHistory(40);
            if (h.success && h.messages.length) {
                setMessages(h.messages.map((m) => ({
                    id: mid(), ts: Date.parse(m.at) || Date.now(),
                    ...(m.role === "user"
                        ? { role: "user", text: m.text }
                        : { role: "bot", replies: [{ type: "text", markdown: true, text: m.text }] }),
                })).concat([{ id: mid(), ts: Date.now(), role: "system", text: "Earlier conversation restored from the bot's memory." }]));
            }
        }
    }, [onAuthExpired, messages.length]);

    useEffect(() => {
        if (!open || link !== "unknown") return;
        checkLink();
    }, [open, link, checkLink]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open) {
                if (maximised) setMaximised(false); else setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, maximised]);

    /* ── sending ── */
    const push = (msg) => setMessages((prev) => [...prev, { id: mid(), ts: Date.now(), ...msg }]);

    const send = useCallback(async (raw) => {
        const text = String(raw ?? input).trim();
        if (!text || sending) return;
        setInput("");
        push({ role: "user", text });

        if (text === "/reset" || text === "/clear") {
            // /reset clears the bot's memory of this chat too — send it on,
            // then clear our local transcript so both sides start fresh.
            setSending(true);
            const res = await sendChatMessage("/reset", { ticker });
            setSending(false);
            if (res.authExpired) { onAuthExpired(); return; }
            setMessages([{ id: mid(), ts: Date.now(), role: "system", text: "Conversation cleared." }]);
            return;
        }

        setSending(true);
        const res = await sendChatMessage(text, { ticker });
        setSending(false);

        if (res.authExpired) { onAuthExpired(); return; }
        if (!res.success) {
            setLink(res.offline ? "offline" : link);
            push({ role: "bot", replies: [{ type: "text", markdown: false, text: `⚠️ ${res.error || "Something went wrong."}` }] });
            return;
        }
        setLink("online");
        push({ role: "bot", replies: res.replies, sent: res.sent, typed: text, busy: res.busy });
        if (!open) setUnread((n) => n + 1);
    }, [input, sending, ticker, onAuthExpired, link, open]);

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    };

    if (!chatConfigured()) return null;

    /* ── layout ── */
    const panelStyle = maximised
        ? { position: "fixed", inset: 12, zIndex: 1400, borderRadius: 14 }
        : {
            position: "fixed", right: 20, bottom: 88, zIndex: 1400, borderRadius: 14,
            width: "min(420px, calc(100vw - 32px))", height: "min(620px, calc(100vh - 120px))",
        };

    const quick = ticker
        ? QUICK_WITH_TICKER.map(([label, cmd]) => [label, `${cmd} ${ticker}`])
        : QUICK_GENERAL;

    return (
        <>
            {/* Launcher */}
            {!open && (
                <Tooltip title="Research chat — the same bot as Telegram" placement="left" arrow>
                    <button
                        type="button"
                        aria-label="Open research chat"
                        onClick={() => setOpen(true)}
                        style={{
                            position: "fixed", right: 20, bottom: 20, zIndex: 1400,
                            width: 54, height: 54, borderRadius: 27, border: "none", cursor: "pointer",
                            backgroundColor: "var(--color-primary)", color: "#fff",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <MessageSquare size={24} />
                        {unread > 0 && (
                            <span style={{ position: "absolute", top: -2, right: -2, minWidth: 20, height: 20, borderRadius: 10, padding: "0 6px", fontSize: 11, fontWeight: 700, backgroundColor: "var(--color-error)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {unread}
                            </span>
                        )}
                    </button>
                </Tooltip>
            )}

            {/* Panel */}
            {open && (
                <div
                    role="dialog"
                    aria-label="Research chat"
                    style={{
                        ...panelStyle,
                        display: "flex", flexDirection: "column", overflow: "hidden",
                        backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)",
                        border: "1px solid var(--color-border-primary)", boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
                    }}
                >
                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px 10px 14px", borderBottom: "1px solid var(--color-border-primary)", backgroundColor: "var(--color-surface)" }}>
                        <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: "var(--color-primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Bot size={18} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Factor Alpha research</div>
                            <div style={{ fontSize: 11.5, color: "var(--color-text-muted)", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {link === "online" ? <Wifi size={12} color="var(--color-success)" /> : link === "offline" ? <WifiOff size={12} color="var(--color-error)" /> : <CircularProgress size={10} />}
                                {link === "online" ? `Same bot as Telegram${who?.user_name || who?.name ? ` · ${who.user_name || who.name}` : ""}` : link === "offline" ? "Bot unreachable" : "Connecting…"}
                                {ticker && <Chip label={ticker} size="small" sx={{ height: 18, fontSize: 10.5, fontWeight: 700, ml: 0.5, bgcolor: "var(--color-primary)", color: "#fff" }} />}
                            </div>
                        </div>
                        <Tooltip title="Clear conversation (also clears the bot's memory of it)" arrow>
                            <IconButton size="small" onClick={() => send("/reset")} disabled={sending} sx={{ color: "var(--color-text-secondary)" }} aria-label="Clear conversation">
                                <RotateCcw size={16} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={maximised ? "Restore" : "Maximise — tables get room"} arrow>
                            <IconButton size="small" onClick={() => setMaximised((m) => !m)} sx={{ color: "var(--color-text-secondary)" }} aria-label={maximised ? "Restore chat size" : "Maximise chat"}>
                                {maximised ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close (Esc)" arrow>
                            <IconButton size="small" onClick={() => { setOpen(false); setMaximised(false); }} sx={{ color: "var(--color-text-secondary)" }} aria-label="Close chat">
                                <X size={18} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/* Messages */}
                    <div
                        ref={listRef}
                        style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: maximised ? "16px 20px" : "12px 12px", display: "flex", flexDirection: "column", gap: 12 }}
                    >
                        {messages.length === 0 && (
                            <div style={{ margin: "auto", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, maxWidth: 420, lineHeight: 1.6 }}>
                                <Bot size={36} style={{ opacity: 0.4, marginBottom: 8 }} />
                                <div style={{ fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 4 }}>Ask the research bot</div>
                                Slash commands work exactly as in Telegram — <code>/look NVDA</code>, <code>/fa</code>, <code>/iv</code>, <code>/news</code> — or ask in plain English.
                                {ticker ? <> You're on <strong>{ticker}</strong>, so a bare <code>/fa</code> or "how does it look" is about it.</> : null}
                                <div style={{ marginTop: 6, fontSize: 12 }}>It remembers your conversation and what you tell it about your positions and horizon — type <code>/memory</code> to see. No price targets, no buy/sell calls — analysis only.</div>
                            </div>
                        )}
                        {messages.map((m) => (
                            <ChatMessage key={m.id} message={m} dense={!maximised} onAuthExpired={onAuthExpired} />
                        ))}
                        {sending && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-muted)", fontSize: 12.5, paddingLeft: 4 }}>
                                <CircularProgress size={14} /> Working… scoring or a report can take a minute.
                            </div>
                        )}
                    </div>

                    {/* Quick commands */}
                    <div style={{ display: "flex", gap: 6, padding: "8px 12px 0", overflowX: "auto", flexShrink: 0, scrollbarWidth: "none" }}>
                        {quick.map(([label, cmd]) => (
                            <Chip
                                key={cmd}
                                label={label}
                                size="small"
                                clickable
                                disabled={sending}
                                onClick={() => send(cmd)}
                                title={cmd}
                                sx={{ fontSize: 11.5, height: 24, bgcolor: "var(--color-surface)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)", "&:hover": { bgcolor: "var(--color-surface-hover)" } }}
                            />
                        ))}
                    </div>

                    {/* Composer */}
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "10px 12px 12px", flexShrink: 0 }}>
                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            rows={1}
                            placeholder={ticker ? `Ask about ${ticker}, or /look, /fa, /iv…` : "Ask a research question, or type / for a command…"}
                            aria-label="Message the research bot"
                            style={{
                                flex: 1, resize: "none", maxHeight: 120, minHeight: 40, padding: "10px 12px", borderRadius: 10, fontSize: 14, lineHeight: 1.4,
                                fontFamily: "inherit", outline: "none", color: "var(--color-text-primary)",
                                backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-secondary)",
                            }}
                            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${Math.min(120, e.target.scrollHeight)}px`; }}
                        />
                        <Tooltip title="Send (Enter)" arrow>
                            <span>
                                <IconButton
                                    onClick={() => send()}
                                    disabled={sending || !input.trim()}
                                    aria-label="Send"
                                    sx={{ width: 40, height: 40, bgcolor: "var(--color-primary)", color: "#fff", "&:hover": { bgcolor: "var(--color-primary)", opacity: 0.9 }, "&.Mui-disabled": { bgcolor: "var(--color-border-primary)", color: "var(--color-text-muted)" } }}
                                >
                                    {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Send size={18} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatWidget;
